import { z } from 'zod';
import { es, en } from 'chrono-node';
import {
  type AssistantConfiguration,
  type SourceFact,
  type ConversationMessage,
  AssistantError,
  calendarWorkingHours,
} from './assistant';
import { buyerEvidenceSchema, type AppointmentExpression } from './sales-turn';
import { appointmentTimeWindowSchema } from './appointment-time-window';
import { calendarAvailabilityEvidence } from './appointment-evidence';
import {
  calendarIdentity,
  calendarFactSource,
} from './appointment-configuration';

export const appointmentPreferenceSchema = z
  .object({
    timezone: z.string().min(1),
    calendarTimezone: z.string().min(1).optional(),
    localDate: z.iso.date().nullable(),
    weekday: z.number().int().min(0).max(6).nullable(),
    hours: z.array(z.number().int().min(0).max(23)).min(1).max(2).nullable(),
    minute: z.number().int().min(0).max(59).nullable(),
    timeWindow: appointmentTimeWindowSchema.nullable().optional(),
    occurrence: z.enum(['first', 'second']).nullable().optional(),
    dateEvidence: buyerEvidenceSchema.nullable(),
    timeEvidence: buyerEvidenceSchema.nullable(),
    needsClarification: z
      .array(z.enum(['date', 'time', 'timezone']))
      .max(3)
      .default([]),
  })
  .strict();
export type AppointmentPreference = z.infer<typeof appointmentPreferenceSchema>;

export const appointmentSelectionSchema = z
  .object({
    connectionId: z.string().min(1).nullable(),
    calendarId: z.string().min(1).nullable(),
    poolId: z.string().uuid().optional(),
    serviceId: z.string().nullable(),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    timezone: z.string().min(1),
    durationMinutes: z.number().int().positive(),
    localDate: z.iso.date(),
    localTime: z.string().regex(/^\d{2}:\d{2}$/u),
    offeredAt: z.iso.datetime({ offset: true }),
    evidence: buyerEvidenceSchema,
  })
  .strict();
export type AppointmentSelection = z.infer<typeof appointmentSelectionSchema>;

// The days on which a delivered reply offered appointment times, as the
// application handed them to the writer. It is kept with the buyer context of
// that reply: a later message that names only a time means one of these days.
// The wording of the reply is never read back to find them.
export const appointmentOfferSchema = z
  .object({
    // The customer message that reply answered.
    answeredMessageId: z.string().min(1),
    timezone: z.string().min(1),
    localDates: z.array(z.iso.date()).min(1).max(31),
  })
  .strict();
export type AppointmentOffer = z.infer<typeof appointmentOfferSchema>;

// The offer to keep with a delivered reply: the days it was written to offer,
// bound to the customer message it answers. A reply that offered no times
// leaves none, so the offer of an earlier reply never outlives it.
export function deliveredAppointmentOffer(
  offer: Omit<AppointmentOffer, 'answeredMessageId'> | null | undefined,
  messages: readonly Pick<ConversationMessage, 'id' | 'role'>[]
): AppointmentOffer | null {
  const answered = messages.filter((item) => item.role === 'customer').at(-1);
  return offer && answered
    ? { answeredMessageId: answered.id, ...offer }
    : null;
}

type Calendar = NonNullable<AssistantConfiguration['calendar']>;
export function localAppointmentTime(startsAt: string, timezone: string) {
  const parts = new Map(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(startsAt))
      .map((part) => [part.type, part.value])
  );
  return {
    localDate: `${parts.get('year')}-${parts.get('month')}-${parts.get('day')}`,
    localTime: `${parts.get('hour')}:${parts.get('minute')}`,
  };
}

const weekdays = [
  'domingo',
  'lunes',
  'martes',
  'miercoles',
  'jueves',
  'viernes',
  'sabado',
];
const englishWeekdays = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export interface AppointmentReference {
  now: Date;
  timezone: string;
}

export function appointmentTimezones(text: string): string[] {
  const normalized = ` ${text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/_/gu, ' ')} `;
  const aliases: [string, string][] = [
    ['nueva york', 'America/New_York'],
    ['peru', 'America/Lima'],
    ...Intl.supportedValuesOf('timeZone').map((zone): [string, string] => [
      zone.split('/').at(-1)!.replace(/_/gu, ' ').toLowerCase(),
      zone,
    ]),
  ];
  return [
    ...new Set(
      aliases
        .filter(([name]) =>
          new RegExp(
            `(?<![\\p{L}])${name.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&')}(?![\\p{L}])`,
            'u'
          ).test(normalized)
        )
        .map(([, zone]) => zone)
    ),
  ];
}

function expressedTimezone(
  text: string,
  calendarTimezone: string
): string | null {
  const zones = new Set(appointmentTimezones(text));
  // The configured business zone can be mentioned as the conversion target.
  if (zones.size > 1) zones.delete(calendarTimezone);
  if (
    zones.size > 1 ||
    (!zones.size &&
      /(?<![\p{L}])(?:cst|ist|bst|ast|pst|est|mst|cdt|edt|pdt|mdt)(?![\p{L}])/iu.test(
        text
      ))
  )
    throw new AssistantError('APPOINTMENT_TIMEZONE_AMBIGUOUS', 409);
  return [...zones][0] ?? null;
}

export function appointmentPreferenceInTimezone(
  preference: AppointmentPreference,
  timezone: string
): AppointmentPreference {
  if (preference.timezone === timezone) return preference;
  if (preference.timeWindow) {
    const endpoints = [
      preference.timeWindow.startsAt,
      preference.timeWindow.endsAt,
    ].map((clock) => {
      const [hour, minute] = clock.split(':').map(Number);
      return appointmentPreferenceInTimezone(
        { ...preference, timeWindow: null, hours: [hour!], minute: minute! },
        timezone
      );
    });
    if (endpoints[0]!.localDate !== endpoints[1]!.localDate)
      throw new AssistantError(
        'APPOINTMENT_PREFERENCE_CLARIFICATION_REQUIRED',
        409
      );
    const clocks = endpoints.map(
      (endpoint) =>
        `${String(endpoint.hours![0]).padStart(2, '0')}:${String(endpoint.minute).padStart(2, '0')}`
    );
    const window = appointmentTimeWindowSchema.safeParse({
      startsAt: clocks[0],
      endsAt: clocks[1],
    });
    if (!window.success)
      throw new AssistantError(
        'APPOINTMENT_PREFERENCE_CLARIFICATION_REQUIRED',
        409
      );
    return {
      ...preference,
      timezone,
      localDate: endpoints[0]!.localDate,
      weekday: null,
      timeWindow: window.data,
    };
  }
  if (
    !preference.localDate ||
    preference.hours?.length !== 1 ||
    preference.minute === null
  )
    throw new AssistantError(
      'APPOINTMENT_PREFERENCE_CLARIFICATION_REQUIRED',
      409
    );
  const wanted = `${String(preference.hours[0]).padStart(2, '0')}:${String(preference.minute).padStart(2, '0')}`;
  const center = Date.parse(`${preference.localDate}T${wanted}:00Z`);
  const instants: string[] = [];
  // IANA offsets and daylight-saving rules are evaluated for the requested
  // date. A nonexistent or repeated wall-clock time requires clarification.
  for (let offset = -14 * 60; offset <= 14 * 60; offset += 15) {
    const instant = new Date(center + offset * 60_000).toISOString();
    const local = localAppointmentTime(instant, preference.timezone);
    if (local.localDate === preference.localDate && local.localTime === wanted)
      instants.push(instant);
  }
  if (
    instants.length !== 1 &&
    !(instants.length === 2 && preference.occurrence)
  )
    throw new AssistantError(
      'APPOINTMENT_PREFERENCE_CLARIFICATION_REQUIRED',
      409
    );
  const instant =
    instants[
      preference.occurrence === 'second' && instants.length === 2 ? 1 : 0
    ]!;
  const local = localAppointmentTime(instant, timezone);
  const [hour, minute] = local.localTime.split(':').map(Number);
  return {
    ...preference,
    timezone,
    localDate: local.localDate,
    hours: [hour!],
    minute: minute!,
    weekday: null,
  };
}

function appointmentDate(
  text: string,
  reference: AppointmentReference,
  resolveWeekday = false,
  previousLocalDate?: string | null
) {
  const normalized = text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const shortDay = new RegExp(
    `\\b(${[...weekdays, ...englishWeekdays].join('|')})\\s+(\\d{1,2})(?=\\s*(?:$|[,.;:()?!¿¡]|a las?\\b|a la\\b|at\\b))`,
    'u'
  ).exec(normalized);
  if (shortDay) {
    const relativeDay = /\b(hoy|today|ma[ñn]ana|tomorrow)\s*$/u.exec(
      normalized.slice(0, shortDay.index)
    );
    const localToday = localAppointmentTime(
      reference.now.toISOString(),
      reference.timezone
    ).localDate;
    const relative = relativeDay ? new Date(`${localToday}T00:00:00Z`) : null;
    if (relative && ['manana', 'tomorrow'].includes(relativeDay![1]!))
      relative.setUTCDate(relative.getUTCDate() + 1);
    const relativeDate = relative?.toISOString().slice(0, 10);
    const weekday = [...weekdays, ...englishWeekdays].indexOf(shortDay[1]!) % 7;
    const upcoming = new Date(`${localToday}T00:00:00Z`);
    upcoming.setUTCDate(
      upcoming.getUTCDate() + ((weekday - upcoming.getUTCDay() + 7) % 7)
    );
    const upcomingDate = upcoming.toISOString().slice(0, 10);
    const anchor = relativeDate ?? previousLocalDate ?? upcomingDate;
    const candidate = `${anchor.slice(0, 8)}${shortDay[2]!.padStart(2, '0')}`;
    const date = new Date(`${candidate}T00:00:00Z`);
    return (!relativeDate || candidate === relativeDate) &&
      (Boolean(relativeDate || previousLocalDate) ||
        candidate === upcomingDate) &&
      Number.isFinite(date.getTime()) &&
      date.toISOString().startsWith(candidate) &&
      date.getUTCDay() === weekday
      ? candidate
      : undefined;
  }
  const sameWeekday = new RegExp(
    `\\b(?:mismo|same)\\s+(${[...weekdays, ...englishWeekdays].join('|')})(?=\\s*(?:$|[,.;:()?!¿¡]|a las?\\b|a la\\b|at\\b))`,
    'u'
  ).exec(normalized);
  if (sameWeekday) {
    const weekday =
      [...weekdays, ...englishWeekdays].indexOf(sameWeekday[1]!) % 7;
    return previousLocalDate &&
      new Date(`${previousLocalDate}T00:00:00Z`).getUTCDay() === weekday
      ? previousLocalDate
      : undefined;
  }
  const local = localAppointmentTime(
    reference.now.toISOString(),
    reference.timezone
  );
  // Parse calendar dates in the buyer's local calendar, independent of the
  // worker's timezone. Hours are resolved separately against actual slots.
  const clock = {
    instant: new Date(`${local.localDate}T00:00:00Z`),
    timezone: 0,
  };
  // "De la mañana" qualifies the hour; chrono otherwise also reads mañana
  // as tomorrow and can contradict an explicit date such as sábado 3 de octubre.
  const dates = new Set<string>();
  const negatedBefore = (before: string) =>
    /\b(?:no|not|ni|en vez de|instead of)\s*(?:el|the)?\s*$/iu.test(before);
  const dateText = text
    .replace(/\b(?:de|por|en) la (?:mañana|manana|tarde|noche)\b/giu, '')
    // Chrono reads only the second word of "pasado mañana", as tomorrow.
    .replace(
      /\bpasado\s+ma[ñn]ana\b|\b(?:the\s+)?day after tomorrow\b/giu,
      (_match, offset: number, whole: string) => {
        if (!negatedBefore(whole.slice(0, offset)))
          dates.add(
            new Date(clock.instant.getTime() + 2 * 86_400_000)
              .toISOString()
              .slice(0, 10)
          );
        return ',';
      }
    )
    .replace(/\bmanana\b/giu, 'mañana');
  const parsed = [
    ...es.parse(dateText, clock, { forwardDate: true }),
    ...en.GB.parse(dateText, clock, { forwardDate: true }),
  ];
  for (const result of parsed) {
    // Incidental expressions of the present are not appointment dates. Only
    // Chrono's actual date span, not unrelated words elsewhere, supplies a day.
    if (/\b(?:ahora|now)\b/iu.test(result.text)) continue;
    // A square/cubic-metre value is a measurement, not a relative minute offset.
    if (
      /\bm$/iu.test(result.text) &&
      /^\s*[²³]/u.test(dateText.slice(result.index + result.text.length))
    )
      continue;
    const before = dateText.slice(0, result.index);
    // Bare calendar abbreviations also occur in ordinary prose ("vista al mar").
    // Inside a sentence they need an explicit temporal cue, not just a parser match.
    if (
      !result.start.isCertain('day') &&
      /^[\p{L}]{2,3}[.,]?$/u.test(result.text.trim()) &&
      before.trim() &&
      !/\b(?:el|este|esta|pr[oó]xim[oa]|on|in|next|this)\s+$/iu.test(before)
    )
      continue;
    if (negatedBefore(before)) continue;
    // "Hoy no puedo" and "hoy no, mejor mañana" rule a day out.
    if (
      /^\s*(?:no(?:\s+(?:puedo|podre|podré|voy a poder|me es posible|es posible|tengo tiempo)\b|\s*[,.;!]|\s*$)|not possible\b|(?:i\s+)?can(?:'|’)?t\b|(?:doesn|won)(?:'|’)?t work\b)/iu.test(
        dateText.slice(result.index + result.text.length)
      )
    )
      continue;
    const start = result.start;
    const calendarDate = start.isCertain('day') || start.isCertain('month');
    const relativeWeek =
      start.isCertain('weekday') &&
      (resolveWeekday ||
        /\b(?:este|esta|pr[oó]xim[oa]|siguiente|que viene|next|this(?: coming)?)\b/iu.test(
          result.text
        ));
    if (!calendarDate && !relativeWeek) continue;
    if (result.end) return undefined;
    const year = start.get('year'),
      month = start.get('month'),
      day = start.get('day');
    if (year === null || month === null || day === null) return undefined;
    dates.add(
      `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    );
  }
  return dates.size > 1 ? undefined : ([...dates][0] ?? null);
}

// Resolve explicit buyer times against actual slots. Bare numbers such as a
// phone, price or apartment code are deliberately not time selections.
export function buyerAppointmentPreference(
  text: string,
  reference?: AppointmentReference,
  previous?: AppointmentPreference | null
) {
  const numbers: Record<string, string> = {
    una: '1',
    uno: '1',
    dos: '2',
    tres: '3',
    cuatro: '4',
    cinco: '5',
    seis: '6',
    siete: '7',
    ocho: '8',
    nueve: '9',
    diez: '10',
    once: '11',
    doce: '12',
  };
  const normalized = text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\b(?:al )?mediodia\b|\bnoon\b/gu, 'a las 12 pm')
    .replace(/\bmedianoche\b|\bmidnight\b/gu, 'a las 12 am')
    .replace(
      /\b(a las?|alrededor de las?|cerca de las?) (una|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce)\b/gu,
      (_whole, prefix: string, number: string) => `${prefix} ${numbers[number]}`
    )
    .replace(/(\d{1,2}) y media\b/gu, '$1:30')
    .replace(/(\d{1,2}) y cuarto\b/gu, '$1:15');
  const positive = normalized.split(/\bsino\b/u).at(-1)!;
  const matches = [
    ...positive.matchAll(
      /(?:\b(?:a las?|alrededor de las?|cerca de las?)\s+|\bat\s+|\b)(\d{1,2})(?::([0-5]\d))?\s*(a\.?\s*m\.?|p\.?\s*m\.?|de la (?:manana|tarde|noche))?/gu
    ),
  ].filter((match) => {
    const explicit =
      /^(?:a las?|alrededor de las?|cerca de las?|at)\s/u.test(match[0]) ||
      match[2] ||
      match[3];
    const before = positive.slice(0, match.index);
    const rejected =
      /\b(?:no|not|ni|en vez de|instead of)\s*(?:a las?|las?|at)?\s*$/u.test(
        before
      );
    return explicit && !rejected;
  });
  if (matches.length !== 1) return null;
  const match = matches[0]!;
  let hour = Number(match[1]);
  if (hour > 23) return null;
  const period = match[3]?.replace(/[.\s]/gu, '');
  if (period && hour > 12) return null;
  if (
    period?.startsWith('p') ||
    period?.includes('tarde') ||
    period?.includes('noche')
  )
    hour = (hour % 12) + 12;
  if (period?.startsWith('a') || period?.includes('manana')) hour %= 12;
  const hours =
    period || hour > 12 || hour === 0 ? [hour] : [hour % 12, (hour % 12) + 12];
  const weekday = weekdays.findIndex((day, index) =>
    new RegExp(`\\b(?:${day}|${englishWeekdays[index]})\\b`, 'u').test(positive)
  );
  const date = reference
    ? appointmentDate(
        text
          .toLowerCase()
          .split(/\bsino\b/u)
          .at(-1)!,
        reference,
        false,
        previous?.localDate
      )
    : (/\b(\d{4}-\d{2}-\d{2})\b/u.exec(positive)?.[1] ?? null);
  if (reference && date === undefined) return null;
  return {
    hours,
    timeWindow: null,
    minute: Number(match[2] ?? 0),
    weekday:
      weekday === -1 ? (date ? null : (previous?.weekday ?? null)) : weekday,
    localDate: date ?? (weekday === -1 ? (previous?.localDate ?? null) : null),
  };
}

const clockPattern = String.raw`\d{1,2}(?::[0-5]\d)?(?:\s*[ap]\.?\s?m\.?(?!\p{L}))?(?:\s*(?:h|hrs?|horas?)(?!\p{L}))?`;
const explicitClockPattern = String.raw`\d{1,2}(?::[0-5]\d(?:\s*[ap]\.?\s?m\.?(?!\p{L}))?|\s*[ap]\.?\s?m\.?(?!\p{L}))`;
const dayPattern = String.raw`(?:lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bados?|domingos?|mondays?|tuesdays?|wednesdays?|thursdays?|fridays?|saturdays?|sundays?)`;
// Spans that state hours or a window, never one time: "de 8:00 a 17:00",
// "entre las 9 y las 11", "hasta las 12:00" and "de lunes a viernes".
const appointmentRanges = [
  new RegExp(
    String.raw`(?:\b(?:de|desde|from)\s+(?:las?\s+)?${clockPattern}|${explicitClockPattern})\s*(?:a|hasta|to|-|–|—)\s*(?:las?\s+)?${clockPattern}`,
    'giu'
  ),
  new RegExp(
    String.raw`\b(?:entre|between)\s+(?:las?\s+)?${clockPattern}\s*(?:y|and)\s*(?:las?\s+)?${clockPattern}`,
    'giu'
  ),
  new RegExp(
    String.raw`\b(?:desde|hasta|a partir de|antes de|despu[eé]s de|from|until|till|before|after)\s+(?:las?\s+)?${clockPattern}`,
    'giu'
  ),
  new RegExp(
    String.raw`\b${dayPattern}\s*(?:a|al|hasta|to|through|-|–|—)\s*${dayPattern}`,
    'giu'
  ),
];
const offeredClock = new RegExp(
  String.raw`(?:\b(?:a\s+las?|alrededor de las?|cerca de las?|at)\s+)?${explicitClockPattern}(?:\s*de la (?:mañana|manana|tarde|noche))?|\b(?:a\s+las?|at)\s+(?:\d{1,2}|una|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce)\b(?:\s*(?:[ap]\.?\s?m\.?(?!\p{L})|de la (?:mañana|manana|tarde|noche)))?|\b(?:al\s+)?mediod[ií]a\b|\bnoon\b`,
  'giu'
);

export interface OfferedAppointmentTime {
  words: string;
  hours: number[];
  minute: number;
  localDate: string | null | undefined;
  weekday: number | null;
}

// The times a text names for an appointment, each with the day its words give
// it: undefined when they name a day that does not resolve, such as a weekday
// that is not that date. Opening hours and windows are not such times. A time
// with no day of its own keeps the day of an earlier time in its sentence.
export function offeredAppointmentTimes(
  text: string,
  reference?: AppointmentReference
): OfferedAppointmentTime[] {
  let work = text;
  for (const range of appointmentRanges)
    work = work.replace(range, (match) => ' '.repeat(match.length));
  // Periods of "a. m." and of decimals do not end a sentence.
  const sentences = work
    .replace(
      /\b([ap])\.(\s?m)(\.?)/giu,
      (_whole, letter: string, meridiem: string, dot: string) =>
        `${letter}·${meridiem}${dot ? '·' : ''}`
    )
    .replace(/(\d)\.(\d)/gu, '$1·$2');
  const times: OfferedAppointmentTime[] = [];
  for (const sentence of sentences.matchAll(/[^.;!?¿¡\n]+/gu)) {
    const start = sentence.index;
    const words = work.slice(start, start + sentence[0].length);
    if (
      /\b(?:abrimos|abre|abren|cerramos|cierra|cierran|opens?|closes?|horario de atenci[oó]n|opening hours|business hours)\b/iu.test(
        words
      )
    )
      continue;
    let clauseStart = 0;
    let previous: OfferedAppointmentTime | null = null;
    for (const clock of words.matchAll(offeredClock)) {
      const clockEnd = clock.index + clock[0].length;
      const time = buyerAppointmentPreference(clock[0]);
      const clause = words.slice(clauseStart, clockEnd);
      clauseStart = clockEnd;
      if (!time) continue;
      const dated = reference
        ? buyerAppointmentPreference(clause, reference)
        : null;
      const own = reference
        ? dated
          ? { localDate: dated.localDate, weekday: dated.weekday }
          : { localDate: undefined, weekday: null }
        : { localDate: null, weekday: null };
      const inherited: Pick<OfferedAppointmentTime, 'localDate' | 'weekday'> =
        own.localDate === null && own.weekday === null && previous
          ? { localDate: previous.localDate, weekday: previous.weekday }
          : own;
      const offered: OfferedAppointmentTime = {
        words: clock[0].trim(),
        hours: time.hours,
        minute: time.minute,
        ...inherited,
      };
      times.push(offered);
      previous = offered;
    }
  }
  return times;
}

function appointmentTimeWindow(
  text: string,
  calendar?: Calendar,
  localDate?: string | null
) {
  const normalized = text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const range =
    /\bentre (?:las? )?(\d{1,2}(?::[0-5]\d)?(?:\s*[ap]\.?\s*m\.?)?) y (?:las? )?(\d{1,2}(?::[0-5]\d)?(?:\s*[ap]\.?\s*m\.?)?)\b/u.exec(
      normalized
    );
  if (!range) {
    const boundary =
      /\b(despues de|antes de|after|before) (?:las? )?(\d{1,2}(?::[0-5]\d)?(?:\s*[ap]\.?\s*m\.?)?)/u.exec(
        normalized
      );
    if (!boundary) {
      // A day period is a search window, never a chosen booking time. Keep
      // explicit clocks ahead of periods such as "a las 10 de la mañana".
      if (buyerAppointmentPreference(text)) return null;
      const morning = /\b(?:por|de|en) la manana\b|\bmorning\b/u.test(
        normalized
      );
      const afternoon = /\b(?:por|de|en) la tarde\b|\bafternoon\b/u.test(
        normalized
      );
      if (morning && afternoon) return undefined;
      if (
        /\b(?:no|not|ni)\s+(?:(?:por|de|en) la (?:manana|tarde)|(?:in the )?(?:morning|afternoon))\b/u.test(
          normalized
        )
      )
        return undefined;
      return morning
        ? { startsAt: '00:00', endsAt: '12:00' }
        : afternoon
          ? { startsAt: '12:00', endsAt: '23:59' }
          : null;
    }
    const explicit = buyerAppointmentPreference(text);
    const boundaryClock = buyerAppointmentPreference(`a las ${boundary[2]}`);
    if (
      explicit &&
      boundaryClock &&
      (JSON.stringify(explicit.hours) !== JSON.stringify(boundaryClock.hours) ||
        explicit.minute !== boundaryClock.minute)
    )
      return null;
    if (!calendar || !localDate) return undefined;
    const clock = boundaryClock;
    const weekday = new Date(`${localDate}T00:00:00Z`).getUTCDay();
    const working = calendarWorkingHours(calendar).find(
      (hours) => hours.day === weekday
    );
    if (!clock || !working) return undefined;
    const after = ['despues de', 'after'].includes(boundary[1]!);
    const candidates = clock.hours.flatMap((hour) => {
      const minute = hour * 60 + clock.minute + (after ? 1 : 0);
      const value = `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
      const parsed = appointmentTimeWindowSchema.safeParse(
        after
          ? { startsAt: value, endsAt: working.closesAt }
          : { startsAt: working.opensAt, endsAt: value }
      );
      return parsed.success &&
        parsed.data.startsAt >= working.opensAt &&
        parsed.data.endsAt <= working.closesAt
        ? [parsed.data]
        : [];
    });
    return candidates.length === 1 ? candidates[0] : undefined;
  }
  const period = /\b(?:por|de|en) la manana\b/u.test(normalized)
    ? 'am'
    : /\b(?:por|de|en) la (?:tarde|noche)\b/u.test(normalized)
      ? 'pm'
      : '';
  const clocks = [range[1]!, range[2]!].map((value) => {
    const hasPeriod = /[ap]\.?\s*m/u.test(value);
    const qualifier = hasPeriod
      ? ''
      : value.startsWith('12') && period === 'am'
        ? 'pm'
        : period;
    return buyerAppointmentPreference(`a las ${value} ${qualifier}`);
  });
  if (clocks.some((clock) => !clock)) return undefined;
  const start = clocks[0]!,
    finish = clocks[1]!;
  const ambiguous = start.hours.length > 1 || finish.hours.length > 1;
  const windows = start.hours.flatMap((startHour) =>
    finish.hours.flatMap((endHour) => {
      const parsed = appointmentTimeWindowSchema.safeParse({
        startsAt: `${String(startHour).padStart(2, '0')}:${String(start.minute).padStart(2, '0')}`,
        endsAt: `${String(endHour).padStart(2, '0')}:${String(finish.minute).padStart(2, '0')}`,
      });
      if (!parsed.success) return [];
      if (ambiguous) {
        if (!calendar) return [];
        const weekday = localDate
          ? new Date(`${localDate}T00:00:00Z`).getUTCDay()
          : null;
        const fits = calendarWorkingHours(calendar).some(
          (hours) =>
            (weekday === null || hours.day === weekday) &&
            parsed.data.startsAt >= hours.opensAt &&
            parsed.data.endsAt <= hours.closesAt
        );
        if (!fits) return [];
      }
      return [parsed.data];
    })
  );
  return windows.length === 1 ? windows[0] : undefined;
}

export function assertAppointmentExpression(
  message: ConversationMessage,
  expression: AppointmentExpression | null | undefined
): void {
  if (!expression) return;
  const now = new Date(message.occurredAt);
  let passages: string[] | null = null;
  const originalPassages = () => {
    if (passages) return passages;
    const parsed = [
      ...es.parse(message.text, now, { forwardDate: true }),
      ...en.GB.parse(message.text, now, { forwardDate: true }),
    ].sort((left, right) => left.index - right.index);
    passages ??= [
      message.text,
      ...parsed
        .filter(
          (result) =>
            !/\b(?:no|not|ni|en vez de|instead of)\s*(?:el|the|a las?|at)?\s*$/iu.test(
              message.text.slice(0, result.index)
            )
        )
        .flatMap((result) => [
          result.text,
          message.text.slice(
            result.index,
            parsed.find((next) => next.index > result.index)?.index
          ),
        ]),
    ];
    return passages;
  };
  const equivalent = (part: string | null, kind: 'date' | 'time') => {
    if (part === null || message.text.includes(part)) return true;
    if (kind === 'time') {
      const normalized = buyerAppointmentPreference(part);
      return (
        !!normalized &&
        originalPassages().some((passage) => {
          const original = buyerAppointmentPreference(passage);
          return (
            !!original &&
            original.minute === normalized.minute &&
            JSON.stringify(original.hours) === JSON.stringify(normalized.hours)
          );
        })
      );
    }
    // This verifies equivalent notation only; the original message and actual
    // calendar timezone still own the stored preference below.
    const reference = {
      now,
      timezone: expressedTimezone(message.text, 'UTC') ?? 'UTC',
    };
    const normalized = appointmentDate(part, reference, true);
    return (
      !!normalized &&
      originalPassages().some(
        (passage) => appointmentDate(passage, reference, true) === normalized
      )
    );
  };
  if (
    expression.evidence.messageId !== message.id ||
    !message.text.includes(expression.evidence.quote) ||
    !equivalent(expression.dateText, 'date') ||
    !equivalent(expression.timeText, 'time')
  )
    throw new AssistantError('APPOINTMENT_EXPRESSION_NOT_GROUNDED', 502);
  for (const alternative of expression.alternatives ?? [])
    assertAppointmentExpression(message, {
      ...alternative,
      evidence: expression.evidence,
      reset: false,
    });
}

export function appointmentPreferencesForTurn(
  input: Parameters<typeof rememberAppointmentPreference>[0]
): AppointmentPreference[] {
  const primary = rememberAppointmentPreference(input);
  const alternatives = (input.expression?.alternatives ?? []).map(
    (alternative) =>
      rememberAppointmentPreference({
        ...input,
        expression: {
          ...alternative,
          evidence: input.expression!.evidence,
          reset: false,
        },
        previous: primary,
      })
  );
  return [primary, ...alternatives].filter(
    (preference): preference is AppointmentPreference => preference !== null
  );
}

function pairedZonedClocks(text: string, calendarTimezone: string) {
  const clocks = [
    ...text.matchAll(
      /(?<![\d])(?:\d{1,2}:[0-5]\d(?:\s*(?:[ap]\.?\s*m\.?|de la (?:mañana|tarde|noche)))?|\d{1,2}\s*[ap]\.?\s*m\.?)/giu
    ),
  ];
  if (clocks.length < 2) return [];
  const parsed = clocks.map((clock, index) => {
    const clause = text.slice(
      clock.index + clock[0].length,
      clocks[index + 1]?.index
    );
    // A city's mention elsewhere in the explanation does not qualify this clock.
    const qualifier = clause.match(
      /^\s*(?:(?:de|en|hora(?:\s+(?:de|en))?|in|time(?:\s+(?:in|of))?)\s+)?([\p{L}_/]+(?:[ \t]+[\p{L}_/]+){0,2})/u
    )?.[0];
    const time = buyerAppointmentPreference(clock[0]);
    const timezone = expressedTimezone(qualifier ?? '', calendarTimezone);
    return { time, timezone };
  });
  const pairs = parsed.flatMap(({ time, timezone }) =>
    time && timezone ? [{ time, timezone }] : []
  );
  // Repeating the same clock with an explicit zone clarifies the earlier reference.
  const sameClock = parsed.every(
    ({ time }) =>
      time &&
      time.minute === parsed[0]!.time?.minute &&
      time.hours?.join(',') === parsed[0]!.time?.hours?.join(',')
  );
  return pairs.length === parsed.length || sameClock ? pairs : [];
}

export function rememberAppointmentPreference(input: {
  previous?: AppointmentPreference | null | undefined;
  message: ConversationMessage;
  timezone: string;
  expression?: AppointmentExpression | null | undefined;
  calendar?: Calendar | undefined;
}): AppointmentPreference | null {
  const { message, expression } = input;
  assertAppointmentExpression(message, expression);
  if (expression?.reset) return null;
  const prior =
    input.previous &&
    (input.previous.calendarTimezone ?? input.previous.timezone) ===
      input.timezone
      ? input.previous
      : null;
  let statedTimezone: string | null = null;
  let unclearTimezone = false;
  let pairs: ReturnType<typeof pairedZonedClocks> = [];
  try {
    pairs = pairedZonedClocks(message.text, input.timezone);
    statedTimezone =
      pairs[0]?.timezone ?? expressedTimezone(message.text, input.timezone);
  } catch (error) {
    if (
      !(error instanceof AssistantError) ||
      error.code !== 'APPOINTMENT_TIMEZONE_AMBIGUOUS'
    )
      throw error;
    unclearTimezone = true;
  }
  const reference = {
    now: new Date(message.occurredAt),
    timezone: statedTimezone ?? prior?.timezone ?? input.timezone,
  };
  const dateText = expression ? (expression.dateText ?? '') : message.text;
  const timeText = expression ? (expression.timeText ?? '') : message.text;
  const date = appointmentDate(dateText, reference, true, prior?.localDate);
  const time = pairs[0]?.time ?? buyerAppointmentPreference(timeText);
  const window = appointmentTimeWindow(
    timeText,
    input.calendar,
    date ?? prior?.localDate
  );
  const unclearDate =
    date === undefined || Boolean(expression?.dateText && !date);
  let unclearTime =
    window === undefined ||
    (!time &&
      !window &&
      (Boolean(expression?.timeText) ||
        /\b(?:a las?|at)\s+\d|\b\d{1,2}:[0-5]\d\b|\b\d{1,2}\s*[ap]\.?\s*m\b/iu.test(
          message.text
        )));
  if (pairs.length) {
    try {
      const instants = pairs.map((pair) =>
        appointmentPreferenceInTimezone(
          {
            ...pair.time,
            localDate: date ?? prior?.localDate ?? null,
            timezone: pair.timezone,
            dateEvidence: null,
            timeEvidence: null,
            needsClarification: [],
          },
          'UTC'
        )
      );
      unclearTime ||= instants.some(
        (instant) =>
          instant.localDate !== instants[0]!.localDate ||
          instant.hours?.[0] !== instants[0]!.hours?.[0] ||
          instant.minute !== instants[0]!.minute
      );
    } catch (error) {
      if (
        !(error instanceof AssistantError) ||
        error.code !== 'APPOINTMENT_PREFERENCE_CLARIFICATION_REQUIRED'
      )
        throw error;
      unclearTime = true;
    }
  }
  const occurrence =
    /\b(?:segunda ocurrencia|second occurrence|despu[eé]s del cambio de reloj)\b/iu.test(
      message.text
    )
      ? 'second'
      : /\b(?:primera ocurrencia|first occurrence|antes del cambio de reloj)\b/iu.test(
            message.text
          )
        ? 'first'
        : null;
  if (
    !date &&
    !time &&
    !window &&
    !unclearDate &&
    !unclearTime &&
    !statedTimezone &&
    !unclearTimezone &&
    !occurrence
  )
    return prior;
  const evidence = {
    messageId: message.id,
    quote: (expression?.evidence.quote ?? message.text).slice(0, 600),
  };
  return appointmentPreferenceSchema.parse({
    timezone: reference.timezone,
    calendarTimezone: input.timezone,
    localDate: unclearDate ? null : (date ?? prior?.localDate ?? null),
    weekday: date ? null : (time?.weekday ?? prior?.weekday ?? null),
    hours: unclearTime || window ? null : (time?.hours ?? prior?.hours ?? null),
    minute:
      unclearTime || window ? null : (time?.minute ?? prior?.minute ?? null),
    timeWindow:
      unclearTime || (time && !window)
        ? null
        : (window ?? prior?.timeWindow ?? null),
    occurrence:
      occurrence ??
      ((!date || date === prior?.localDate) &&
      (!time ||
        (time.minute === prior?.minute &&
          JSON.stringify(time.hours) === JSON.stringify(prior?.hours))) &&
      (!statedTimezone || statedTimezone === prior?.timezone)
        ? (prior?.occurrence ?? null)
        : null),
    dateEvidence: date ? evidence : (prior?.dateEvidence ?? null),
    timeEvidence: time || window ? evidence : (prior?.timeEvidence ?? null),
    needsClarification: [
      ...(unclearTimezone ||
      (!statedTimezone && prior?.needsClarification.includes('timezone'))
        ? ['timezone']
        : []),
      ...(unclearDate || (!date && prior?.needsClarification.includes('date'))
        ? ['date']
        : []),
      ...(unclearTime ||
      (!time && !window && prior?.needsClarification.includes('time'))
        ? ['time']
        : []),
    ],
  });
}

export function matchesAppointmentPreference(
  text: string,
  startsAt: string,
  timezone: string,
  now: Date
): boolean {
  return (
    appointmentConfirmationChange(text, startsAt, timezone, now) === 'unchanged'
  );
}

export function appointmentConfirmationChange(
  text: string,
  startsAt: string,
  timezone: string,
  now: Date
): 'unchanged' | 'changed' | 'clarify' {
  const local = localAppointmentTime(startsAt, timezone);
  const [hour, minute] = local.localTime.split(':').map(Number);
  const preference = rememberAppointmentPreference({
    previous: {
      timezone,
      calendarTimezone: timezone,
      localDate: local.localDate,
      weekday: null,
      hours: [hour!],
      minute: minute!,
      dateEvidence: null,
      timeEvidence: null,
      needsClarification: [],
    },
    message: {
      id: 'confirmation',
      role: 'customer',
      text,
      occurredAt: now.toISOString(),
    },
    timezone,
  });
  if (!preference || preference.needsClarification.length) return 'clarify';
  if (preference.timeWindow) return 'clarify';
  const proposed = localAppointmentTime(startsAt, preference.timezone);
  const [proposedHour, proposedMinute] = proposed.localTime
    .split(':')
    .map(Number);
  return preference.localDate === proposed.localDate &&
    preference.hours?.includes(proposedHour!) &&
    preference.minute === proposedMinute
    ? 'unchanged'
    : 'changed';
}

// Cancellation accepts a partial date reference; a new booking still needs a
// complete selected slot. Only one matching authoritative appointment is usable.
export function selectAppointmentToCancel<
  T extends {
    actionId: string;
    startsAt: string;
    timezone: string;
  },
>(text: string, appointments: readonly T[], now: Date): T {
  const normalized = text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const mentionedDays = weekdays.flatMap((day, index) =>
    new RegExp(`\\b(?:${day}|${englishWeekdays[index]})\\b`, 'u').test(
      normalized
    )
      ? [index]
      : []
  );
  if (mentionedDays.length > 1)
    throw new AssistantError('APPOINTMENT_SELECTION_REQUIRED', 409);
  const matching = appointments.filter((appointment) => {
    const timezone =
      expressedTimezone(text, appointment.timezone) ?? appointment.timezone;
    const reference = { now, timezone };
    const date = appointmentDate(text, reference);
    if (date === undefined) return false;
    const local = localAppointmentTime(appointment.startsAt, timezone);
    if (date && date !== local.localDate) return false;
    if (mentionedDays.length) {
      const weekday = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'long',
      })
        .format(new Date(appointment.startsAt))
        .toLowerCase();
      if (englishWeekdays[mentionedDays[0]!] !== weekday) return false;
    }
    const time = buyerAppointmentPreference(text, reference);
    if (time)
      return matchesAppointmentPreference(
        text,
        appointment.startsAt,
        timezone,
        now
      );
    // An unparsed explicit time cannot authorize cancelling an arbitrary event.
    return !/\d{1,2}:\d{2}|\ba las?\s+|\bat\s+\d|\d\s*[ap]\.?m\.?/iu.test(text);
  });
  if (matching.length !== 1)
    throw new AssistantError('APPOINTMENT_SELECTION_REQUIRED', 409);
  return matching[0]!;
}

export function selectAppointment(input: {
  message: ConversationMessage;
  facts: readonly SourceFact[];
  calendar: Calendar;
  serviceId?: string | null;
  previous?: AppointmentSelection | null | undefined;
  preference?: AppointmentPreference | null | undefined;
  now: Date;
}): AppointmentSelection | null {
  const storedPreference =
    input.preference &&
    (input.preference.calendarTimezone ?? input.preference.timezone) ===
      input.calendar.timezone
      ? input.preference
      : undefined;
  if (storedPreference?.needsClarification.length) return null;
  let preference = buyerAppointmentPreference(
    input.message.text,
    {
      now: new Date(input.message.occurredAt),
      timezone:
        storedPreference?.timezone ??
        expressedTimezone(input.message.text, input.calendar.timezone) ??
        input.calendar.timezone,
    },
    storedPreference
  );
  if (
    !preference &&
    storedPreference &&
    storedPreference.dateEvidence?.messageId === input.message.id &&
    storedPreference.hours &&
    storedPreference.minute !== null
  ) {
    preference = {
      hours: storedPreference.hours,
      timeWindow: null,
      minute: storedPreference.minute,
      localDate: storedPreference.localDate,
      weekday: storedPreference.weekday,
    };
  }
  if (
    preference &&
    storedPreference?.dateEvidence?.messageId === input.message.id
  )
    preference.localDate = storedPreference.localDate;
  if (!preference) {
    const changedTime =
      /\d{1,2}:\d{2}|\ba las?\s+|\bat \d|\b(?:lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bado|domingo|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/iu.test(
        input.message.text
      );
    return changedTime ? null : (input.previous ?? null);
  }
  const { calendar } = input;
  const found = new Map<string, AppointmentSelection>();
  for (const fact of input.facts) {
    if (
      fact.source !== calendarFactSource(calendar) ||
      input.now.getTime() - Date.parse(fact.observedAt) > 30 * 60_000 ||
      Date.parse(fact.observedAt) > input.now.getTime()
    )
      continue;
    const offered = calendarAvailabilityEvidence(fact);
    if (
      !offered ||
      offered.timezone !== calendar.timezone ||
      (offered.serviceId ?? null) !== (input.serviceId ?? null)
    )
      continue;
    for (const slot of offered.availableSlots) {
      if (
        Date.parse(slot.startsAt) <= input.now.getTime() ||
        (Date.parse(slot.endsAt) - Date.parse(slot.startsAt)) / 60_000 !==
          calendar.durationMinutes
      )
        continue;
      const sourceZone =
        storedPreference?.timezone ??
        expressedTimezone(input.message.text, calendar.timezone) ??
        calendar.timezone;
      const local = localAppointmentTime(slot.startsAt, sourceZone);
      const weekday = englishWeekdays.indexOf(
        new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          timeZone: sourceZone,
        })
          .format(new Date(slot.startsAt))
          .toLowerCase()
      );
      const [hour, minute] = local.localTime.split(':').map(Number);
      if (
        !preference.hours.includes(hour!) ||
        minute !== preference.minute ||
        (preference.weekday !== null && preference.weekday !== weekday) ||
        (preference.localDate && preference.localDate !== local.localDate) ||
        (!preference.localDate &&
          preference.weekday === null &&
          input.previous &&
          input.previous.localDate !== local.localDate)
      )
        continue;
      found.set(slot.startsAt, {
        ...slot,
        ...localAppointmentTime(slot.startsAt, calendar.timezone),
        ...calendarIdentity(calendar),
        serviceId: input.serviceId ?? null,
        timezone: calendar.timezone,
        durationMinutes:
          (Date.parse(slot.endsAt) - Date.parse(slot.startsAt)) / 60_000,
        offeredAt: fact.observedAt,
        evidence: {
          messageId: input.message.id,
          quote: input.message.text.slice(0, 600),
        },
      });
    }
  }
  // Ambiguous AM/PM or day requires a conversational clarification. A correction
  // that has no matching slot clears the old choice instead of reusing it.
  return found.size === 1
    ? appointmentSelectionSchema.parse([...found.values()][0])
    : null;
}

export function bindSelectedAppointment<
  T extends {
    startsAt: string;
    endsAt: string;
    serviceId?: string | null | undefined;
  },
>(request: T, selection: AppointmentSelection | null | undefined): T {
  if (!selection)
    throw new AssistantError('APPOINTMENT_SELECTION_REQUIRED', 409);
  if ((request.serviceId ?? null) !== selection.serviceId)
    throw new AssistantError('APPOINTMENT_SELECTION_SERVICE_MISMATCH', 409);
  return { ...request, startsAt: selection.startsAt, endsAt: selection.endsAt };
}
